"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsTable = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _material = require("@mui/material");
var _DmsFetch = require("../DmsFetch/DmsFetch");
var _DmsTokenHook = require("../DmsTokenHook/DmsTokenHook");
var _DmsExport = require("../DmsExport/DmsExport");
var _DmsTableHeader = require("./DmsTableHeader");
var _DmsTableBody = require("./DmsTableBody");
var _DmsTableFooter = require("./DmsTableFooter");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * DmsTable component for displaying paginated and sortable tables with filtering, dynamic height and toolbar options.
 * @param {Object} props - Component props.
 * @param {boolean} [props.export_table=true] - Flag to enable export functionality.
 * @param {Array} [props.header=[]] - Array of column headers.
 * @param {boolean} [props.managed=false] - Flag indicating if the table is managed (server-side pagination, filtering and sorting).
 * @param {boolean} [props.needs_authentication=true] - Flag indicating if authentication is required for fetching data.
 * @param {number} [props.page_length=10] - Number of rows per page.
 * @param {boolean} [props.paging=true] - Flag to enable pagination.
 * @param {boolean} [props.responsive=false] - Flag to enable responsive table behavior.
 * @param {boolean} [props.show_header=true] - Flag to show table header.
 * @param {string} [props.initial_sort="updated_on DESC"] - Initial sorting order.
 * @param {React.ReactNode} [props.toolbar=null] - Custom toolbar component.
 * @param {string} props.url - API endpoint URL.
 * @param {boolean} [props.scrollWithinTable=false] - Flag to enable internal scroll inside the table container.
 * @param {number} [props.dynamicTableOffset=300] - Offset used to calculate dynamic height (window.innerHeight - offset).
 * @param {Array} [props.pre_filter=null] - Array of static filters always applied to the query.
 * @param {React.RefObject} ref - Forwarded ref object.
 * @returns {JSX.Element} DmsTable component.
 */
const DmsTable = exports.DmsTable = /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let {
    export_table = true,
    header = [],
    managed = false,
    needs_authentication = true,
    page_length = 10,
    paging = true,
    show_header = true,
    initial_sort = "updated_on DESC",
    toolbar = null,
    url,
    scrollWithinTable = false,
    pre_filter = null,
    dynamicTableOffset = 300
  } = _ref;
  const [data, setData] = (0, _react.useState)([]);
  const [pagination, setPagination] = (0, _react.useState)({
    offset: 0,
    limit: page_length,
    total: 0,
    page: 0,
    pages: 0
  });
  const [filters, setFilters] = (0, _react.useState)({});
  const [sort, setSort] = (0, _react.useState)({});
  const [loading, setLoading] = (0, _react.useState)(false);
  const token = (0, _DmsTokenHook.getToken)();
  const [tableHeight, setTableHeight] = (0, _react.useState)("auto");
  const [updatedHeader, setUpdatedHeader] = (0, _react.useState)(header);
  const [fetchingOptions, setFetchingOptions] = (0, _react.useState)(true);
  (0, _react.useEffect)(() => {
    const fetchOptions = async () => {
      setFetchingOptions(true);
      const updatedHeaderWithOptions = await Promise.all(header.map(async col => {
        if (col.control === "select" && col.url) {
          try {
            const response = await (0, _DmsFetch.fetchData)(col.url, "GET", needs_authentication ? token : null);
            const data = response.data.data.rows || response.data.data;
            if (Array.isArray(data)) {
              const formattedMap = Object.fromEntries(data.map(item => {
                var _col$properties, _col$properties2;
                return [item[(_col$properties = col.properties) === null || _col$properties === void 0 ? void 0 : _col$properties.value] || item.pk, item[(_col$properties2 = col.properties) === null || _col$properties2 === void 0 ? void 0 : _col$properties2.label] || item.name];
              }));
              return _objectSpread(_objectSpread({}, col), {}, {
                map: formattedMap
              });
            }
          } catch (error) {
            console.error("Error fetching options for ".concat(col.column_name, ":"), error);
          }
        }
        return col;
      }));
      setUpdatedHeader(updatedHeaderWithOptions);
      setFetchingOptions(false);
    };
    fetchOptions();
  }, [header]);
  /**
   * Dynamically adjusts the table height based on the available window height.
   * Ensures the table fits within the screen while maintaining a minimum height of 200px.
   * Recalculates the height on window resize and removes the listener on component unmount.
   */
  (0, _react.useEffect)(() => {
    const updateHeight = () => {
      const availableHeight = window.innerHeight - dynamicTableOffset;
      setTableHeight(availableHeight > 200 ? availableHeight : 200);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  (0, _react.useEffect)(() => {
    updateTable();
  }, [filters, sort, pagination.page, pagination.limit, url]);

  /**
   * Fetches data from the API based on current filter, sort, and pagination settings.
   */
  const updateTable = async () => {
    setLoading(true);
    const query_url = url + getURIQueryParameters();
    const response = await (0, _DmsFetch.fetchData)(query_url, "GET", needs_authentication ? token : null);
    const responseData = response.data.data || [];
    setData(responseData.rows || responseData);
    setPagination(prev => {
      var _responseData$rows, _responseData$rows2;
      return _objectSpread(_objectSpread({}, prev), {}, {
        total: responseData.total || ((_responseData$rows = responseData.rows) === null || _responseData$rows === void 0 ? void 0 : _responseData$rows.length) || 0,
        pages: Math.ceil((responseData.total || ((_responseData$rows2 = responseData.rows) === null || _responseData$rows2 === void 0 ? void 0 : _responseData$rows2.length) || 0) / prev.limit)
      });
    });
    setLoading(false);
  };

  /**
   * Gets the current header configuration.
   * @returns {Array} Array of column headers.
   */
  const getHeader = () => header;

  /**
   * Gets the current data rows.
   * @returns {Array} Array of data rows.
   */
  const getData = () => data;

  /**
   * Gets data for a specific row.
   * @param {number} rowIndex - Index of the row.
   * @returns {Object} Data for the row.
   */
  const getRowData = rowIndex => data[rowIndex];

  /**
   * Constructs URI query parameters based on current filter, sort, and pagination settings.
   * @param {boolean} [ignorePaging=false] - Flag to ignore pagination parameters.
   * @returns {string} Constructed URI query parameters string.
   */
  const getURIQueryParameters = function () {
    let ignorePaging = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (!managed) return "";
    let res = "?";
    let paging_query = "";
    if (paging && !ignorePaging) {
      paging_query = "offset=".concat(pagination.offset, "&limit=").concat(pagination.limit, "&");
    }
    let all_filters = Array.isArray(pre_filter) ? [...pre_filter] : [];
    for (const name in filters) {
      if (Object.prototype.hasOwnProperty.call(filters, name)) {
        const value = filters[name];
        if (value !== null && value !== undefined && value !== "") {
          all_filters.push({
            name,
            value
          });
        }
      }
    }
    let filter_string = all_filters.length > 0 ? "filter=".concat(encodeURIComponent(JSON.stringify(all_filters))) : "";
    let sort_query = initial_sort;
    for (let column in sort) {
      if (sort[column].length > 0) {
        sort_query = "".concat(column, " ").concat(sort[column]);
      }
    }
    res += "".concat(paging_query).concat(filter_string, "&order_by=").concat(sort_query);
    return res;
  };

  /**
   * Handles column sorting.
   * @param {Object} header - Column header object.
   * @param {string} sortOrder - Sorting order ('ASC' or 'DESC').
   */

  const handleSort = (header, sortOrder) => {
    const column = header.order_column_name || header.column_name;
    setSort({
      [column]: sortOrder
    });
  };

  /**
   * Handles filter changes.
   * @param {string} name - Filter name.
   * @param {any} value - Filter value.
   * @param {Object} [properties={}] - Filter properties.
   */
  const handleFilter = function (name, value) {
    let properties = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    setFilters(prevFilters => {
      const newFilters = _objectSpread({}, prevFilters);
      if (value === null || value === "") {
        delete newFilters[name];
      } else {
        newFilters[name] = value;
      }
      return newFilters;
    });
    setPagination(prev => _objectSpread(_objectSpread({}, prev), {}, {
      page: 0,
      offset: 0
    }));
  };

  /**
   * Handles page change event.
   * @param {object} event - Event object.
   * @param {number} newPage - New page number.
   */
  const handlePageChange = (event, newPage) => {
    setPagination(prev => _objectSpread(_objectSpread({}, prev), {}, {
      page: newPage,
      offset: newPage * prev.limit
    }));
  };

  /**
   * Handles page length change event.
   * @param {object} event - Event object.
   */
  const handlePageLengthChange = event => {
    const newLength = event.target.value;
    setPagination(prev => _objectSpread(_objectSpread({}, prev), {}, {
      limit: newLength,
      offset: Math.floor(prev.offset / newLength) * newLength,
      page: 0
    }));
  };

  /**
   * Sets the current page.
   * @param {number} page - Page number to set.
   */
  const setPage = page => {
    setPagination(prev => _objectSpread(_objectSpread({}, prev), {}, {
      page,
      offset: page * prev.limit
    }));
  };
  const exportUrl = url.replace("/all", "/export").replace("/table", "/export") + getURIQueryParameters();
  if (ref) {
    ref.current = {
      updateTable,
      getHeader,
      getData,
      getRowData,
      needs_authentication,
      pagination,
      url,
      exportUrl,
      setPage
    };
  }
  return /*#__PURE__*/_react.default.createElement(_material.Paper, null, (toolbar || export_table) && /*#__PURE__*/_react.default.createElement(_material.Box, null, toolbar || /*#__PURE__*/_react.default.createElement(_material.Toolbar, null, /*#__PURE__*/_react.default.createElement(_material.Grid, {
    container: true,
    justifyContent: "flex-end"
  }, /*#__PURE__*/_react.default.createElement(_material.Grid, {
    item: true
  }, /*#__PURE__*/_react.default.createElement(_DmsExport.DmsExport, {
    url: exportUrl,
    needs_authentication: needs_authentication,
    group: false
  }))))), /*#__PURE__*/_react.default.createElement(_material.TableContainer, {
    style: {
      maxHeight: scrollWithinTable ? "".concat(tableHeight, "px") : "auto",
      overflowY: scrollWithinTable ? "auto" : "visible"
    }
  }, /*#__PURE__*/_react.default.createElement(_material.Table, {
    stickyHeader: true
  }, show_header && /*#__PURE__*/_react.default.createElement(_DmsTableHeader.DmsTableHeader, {
    header: updatedHeader,
    onSort: handleSort,
    sort: sort,
    ariaControl: url,
    handleFilter: handleFilter
  }), /*#__PURE__*/_react.default.createElement(_DmsTableBody.DmsTableBody, {
    data: data,
    header: updatedHeader,
    loading: loading
  }), paging && /*#__PURE__*/_react.default.createElement(_DmsTableFooter.DmsTableFooter, {
    pagination: pagination,
    handlePageChange: handlePageChange,
    handlePageLengthChange: handlePageLengthChange
  }))));
});
DmsTable.propTypes = {
  export_table: _propTypes.default.bool,
  header: _propTypes.default.arrayOf(_propTypes.default.shape({
    column_name: _propTypes.default.string.isRequired,
    width: _propTypes.default.string,
    hidden: _propTypes.default.bool,
    permissions: _propTypes.default.arrayOf(_propTypes.default.string),
    element: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func]),
    map: _propTypes.default.object,
    properties: _propTypes.default.shape({
      multiple: _propTypes.default.bool,
      column_name: _propTypes.default.string,
      filter: _propTypes.default.shape({
        value: _propTypes.default.string,
        field: _propTypes.default.string,
        join: _propTypes.default.string,
        left: _propTypes.default.string,
        right: _propTypes.default.string
      })
    }),
    function: _propTypes.default.func,
    order_column_name: _propTypes.default.string,
    control: _propTypes.default.string,
    url: _propTypes.default.string
  })),
  managed: _propTypes.default.bool,
  needs_authentication: _propTypes.default.bool,
  page_length: _propTypes.default.number,
  paging: _propTypes.default.bool,
  responsive: _propTypes.default.bool,
  show_header: _propTypes.default.bool,
  initial_sort: _propTypes.default.string,
  toolbar: _propTypes.default.node,
  url: _propTypes.default.string.isRequired,
  scrollWithinTable: _propTypes.default.bool,
  dynamicTableOffset: _propTypes.default.number,
  pre_filter: _propTypes.default.arrayOf(_propTypes.default.shape({
    name: _propTypes.default.string.isRequired,
    value: _propTypes.default.any,
    join: _propTypes.default.string,
    left: _propTypes.default.string,
    right: _propTypes.default.string
  }))
};